import { useId, useRef, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  Input,
  formatDate,
  PageTitle,
  ConfirmDialog,
} from '@dataroom/shared';
import type { SavedChecklistTemplate } from '@dataroom/shared';
import {
  useCreateSavedChecklist,
  useDeleteSavedChecklist,
  useSavedChecklists,
  useTenantPlan,
} from '@/api/hooks';
import { ChecklistProblemsTable } from '@/components/ChecklistProblemsTable';
import { ChecklistTemplateLinks } from '@/components/ChecklistTemplateLinks';
import {
  MAX_CHECKLIST_BYTES,
  checklistCountsLabel,
  checklistProblemsOf,
  closingVaultLocked,
  downloadFromApi,
  isForbidden,
  isUnauthorized,
  nameFromFilename,
  PROFESSIONAL_ONLY_HINT,
  savedChecklistsLocked,
} from '@/lib/checklist';
import { contentTypeOf, fileToBase64 } from '@/lib/file';

/**
 * ADR-0017 "Checklists" — the tenant's SAVED due-diligence checklists: list
 * (counts + created), download each as .xlsx, delete, and upload a new one. Every
 * backing endpoint is tenant-admin only; a 403 renders a plain access message
 * (the nav link is already hidden for non-admins — see Layout).
 */
export function ChecklistsPage() {
  const saved = useSavedChecklists();
  const plan = useTenantPlan();
  // A free-trial or Starter tenant gets the sample layout instead of the ClosingVault
  // checklist (ADR-0044).
  const trial = closingVaultLocked(plan.data);
  // Saving a checklist is Professional only (existing ones stay usable and listed).
  const savedLocked = savedChecklistsLocked(plan.data);
  const create = useCreateSavedChecklist();
  const remove = useDeleteSavedChecklist();
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState<SavedChecklistTemplate | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [encoding, setEncoding] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const fileId = useId();

  if (isUnauthorized(saved.error)) return <Navigate to="/login" replace />;

  const problems = checklistProblemsOf(create.error);
  const createError =
    formError ?? (problems ? null : create.error instanceof ApiError ? create.error.message : null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setNotice(null);
    const trimmed = name.trim();
    if (!trimmed) return setFormError('Give the checklist a name.');
    if (!file) return setFormError('Choose the checklist file (.xlsx).');
    if (file.size > MAX_CHECKLIST_BYTES) {
      return setFormError('That file is larger than 5 MB — checklists are capped at 5 MB.');
    }
    setEncoding(true);
    let contentBase64: string;
    try {
      contentBase64 = await fileToBase64(file);
    } catch {
      setFormError("That file couldn't be read. Please choose it again.");
      return;
    } finally {
      setEncoding(false);
    }
    create.mutate(
      { name: trimmed, filename: file.name, contentType: contentTypeOf(file), contentBase64 },
      {
        onSuccess: (t) => {
          setNotice(`Saved “${t.name}” — ${checklistCountsLabel(t)}.`);
          setName('');
          setFile(null);
          if (fileRef.current) fileRef.current.value = '';
        },
      },
    );
  }

  async function download(t: SavedChecklistTemplate) {
    setDownloadError(null);
    try {
      await downloadFromApi(`/dd-templates/${t.id}/xlsx`, `${t.name}.xlsx`);
    } catch (err) {
      setDownloadError(err instanceof ApiError ? err.message : 'Download failed.');
    }
  }

  // Deleting a saved checklist is irreversible (no restore endpoint), so it
  // asks first — in the shared ConfirmDialog, not a native window.confirm.
  function confirmDelete(t: SavedChecklistTemplate) {
    setDeleting(t);
  }
  const deleteDialog = deleting ? (
    <ConfirmDialog
      danger
      title={`Delete “${deleting.name}”?`}
      confirmLabel="Delete checklist"
      pending={remove.isPending}
      onCancel={() => setDeleting(null)}
      onConfirm={() => remove.mutate(deleting.id, { onSettled: () => setDeleting(null) })}
    >
      <p>Rooms already set up from it keep their checklist. This can’t be undone.</p>
    </ConfirmDialog>
  ) : null;

  if (isForbidden(saved.error)) {
    return (
      <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6">
        <Card>
          <CardHeader>
            <CardTitle as="h1">Checklists</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Only your firm&apos;s owner or rooms admins can manage saved due-diligence checklists.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pending = create.isPending || encoding;
  const formDisabled = pending || savedLocked;
  const list = saved.data ?? [];

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      <div className="flex items-center gap-2">
        <PageTitle>Checklists</PageTitle>
        <HelpTip id="admin.checklists.title" />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Saved checklists</CardTitle>
          <p className="text-sm text-muted-foreground">
            Due-diligence checklists your firm can reuse when setting up a new room. Deleting one
            never changes rooms already set up from it.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {saved.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : saved.error ? (
            <p className="text-sm text-destructive">{saved.error.message}</p>
          ) : list.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 font-medium">Name</th>
                  <th className="py-2 font-medium">Contents</th>
                  <th className="py-2 font-medium">Created</th>
                  <th className="py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium">{t.name}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {t.valid === false ? (
                        // Listed so it can be seen and deleted; it can't seed a room.
                        <span className="text-st-check-fg">
                          Can’t be used: {t.invalidReason ?? 'this checklist is no longer valid'}
                        </span>
                      ) : (
                        checklistCountsLabel(t)
                      )}
                    </td>
                    <td className="py-2 pr-4">{formatDate(t.createdAt)}</td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          className="text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
                          disabled={t.valid === false}
                          onClick={() => void download(t)}
                        >
                          Download (.xlsx)
                        </button>
                        <button
                          type="button"
                          className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                          disabled={remove.isPending && remove.variables === t.id}
                          onClick={() => confirmDelete(t)}
                        >
                          {remove.isPending && remove.variables === t.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-muted-foreground">
              {savedLocked
                ? `No saved checklists yet. Saving checklists for reuse is ${PROFESSIONAL_ONLY_HINT.toLowerCase()}.`
                : 'No saved checklists yet. Upload one below, or tick “Save this checklist for reuse” when you upload a checklist while setting up a room.'}
            </p>
          )}
          {downloadError ? <p className="text-sm text-destructive">{downloadError}</p> : null}
          {remove.error instanceof ApiError ? (
            <p className="text-sm text-destructive">{remove.error.message}</p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Upload a checklist</CardTitle>
          {savedLocked ? (
            <p className="text-sm font-medium text-primary">{PROFESSIONAL_ONLY_HINT}</p>
          ) : null}
          <p className="text-sm text-muted-foreground">
            {trial
              ? 'Build your checklist in Excel (start from the sample layout), then save it here under a name.'
              : 'Fill in the Excel template (or start from the ClosingVault checklist), then save it here under a name.'}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <ChecklistTemplateLinks showDefault={!trial} showSample={trial} />
          <form className="space-y-3" onSubmit={(e) => void submit(e)}>
            <div className="space-y-1.5">
              <label htmlFor={nameId} className="text-sm font-medium">
                Name
              </label>
              <Input
                id={nameId}
                value={name}
                maxLength={120}
                placeholder="e.g. Lower-middle-market services"
                onChange={(e) => setName(e.target.value)}
                disabled={formDisabled}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor={fileId} className="text-sm font-medium">
                Checklist file (.xlsx)
              </label>
              <input
                ref={fileRef}
                id={fileId}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
                disabled={formDisabled}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  create.reset();
                  setFormError(null);
                  // Follow the picked file's name unless the user typed their own.
                  const prevAuto = file ? nameFromFilename(file.name) : '';
                  if (f && (!name.trim() || name === prevAuto)) setName(nameFromFilename(f.name));
                  setFile(f);
                }}
              />
            </div>
            <Button type="submit" disabled={formDisabled}>
              {pending ? 'Saving…' : 'Save checklist'}
            </Button>
          </form>
          {problems ? <ChecklistProblemsTable {...problems} /> : null}
          {createError ? <p className="text-sm text-destructive">{createError}</p> : null}
          {notice ? (
            <p className="text-sm text-st-certified-fg" role="status">
              {notice}
            </p>
          ) : null}
        </CardContent>
      </Card>
      {deleteDialog}
    </div>
  );
}
